import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { financeAPI } from '../../services/api';
import { getCountries } from '../../data/locationData';
import { formatCurrency } from './formatCurrency';

const STEPS = ['Student', 'Plan', 'Pricing', 'Extras', 'Review'];

const PLAN_LABELS = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  six_months: '6 Months',
  yearly: 'Yearly',
};
const BATCH_LABELS = { one_on_one: '1:1', group: 'Group' };

const subscriptionModels = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'six_months', label: '6 Months' },
  { value: 'yearly', label: 'Yearly' },
];
const batchTypes = [
  { value: 'one_on_one', label: '1:1' },
  { value: 'group', label: 'Group' },
];

const countryToCurrency = {
  USA: 'USD',
  India: 'INR',
  Canada: 'CAD',
  UK: 'GBP',
  Australia: 'AUD',
  'New Zealand': 'NZD',
  Singapore: 'SGD',
  Japan: 'JPY',
  UAE: 'AED',
  Germany: 'EUR',
  Indonesia: 'IDR',
  Kuwait: 'KWD',
  Netherlands: 'EUR',
  Oman: 'OMR',
  'Saudi Arabia': 'SAR',
  Sweden: 'SEK',
};

const countryList = getCountries();

const emptyForm = () => ({
  batchType: 'group',
  country: '',
  currency: '',
  subscriptionModel: 'monthly',
  classesPerWeek: 2,
  bonusClasses: 0,
  serviceStartDate: new Date().toISOString().slice(0, 10),
  enrollmentDate: new Date().toISOString().slice(0, 10),
});

/**
 * Shared subscription wizard for Sales (Admission) and Finance.
 */
export default function SubscriptionWizard({
  open,
  onClose,
  onCreated,
  /** Pre-selected student { id, firstName, lastName, email, displayId, country } */
  initialStudent = null,
  /** When true, student step is skipped if initialStudent is set */
  lockStudent = false,
}) {
  const [step, setStep] = useState(0);
  const [studentSearch, setStudentSearch] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [discounts, setDiscounts] = useState([]);
  const [addOns, setAddOns] = useState([]);
  const [notifyParent, setNotifyParent] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(initialStudent && lockStudent ? 1 : 0);
    setSelectedStudent(initialStudent || null);
    setStudentSearch('');
    const country = initialStudent?.country || '';
    setForm({
      ...emptyForm(),
      country,
      currency: countryToCurrency[country] || '',
    });
    setDiscounts([]);
    setAddOns([]);
    setNotifyParent(!!(initialStudent?.email));
    setPreview(null);
    setPreviewError('');
  }, [open, initialStudent, lockStudent]);

  const studentsQuery = useQuery(
    ['wizardStudentsWithoutSub', studentSearch],
    async () => {
      const res = await financeAPI.getStudentsWithoutSubscription({
        search: studentSearch || undefined,
        limit: 100,
      });
      return res.data?.data || res.data || [];
    },
    { enabled: open && step === 0 && !(lockStudent && initialStudent) }
  );

  const discountCatalogQuery = useQuery(
    ['wizardDiscountCatalog'],
    async () => {
      const res = await financeAPI.getDiscountCatalog();
      return (res.data?.data || res.data || []).filter((d) => d.isActive !== false && d.is_active !== false);
    },
    { enabled: open && step >= 3 }
  );

  const addOnCatalogQuery = useQuery(
    ['wizardAddOnCatalog'],
    async () => {
      const res = await financeAPI.getAddOnCatalog();
      return res.data?.data || res.data || [];
    },
    { enabled: open && step >= 3 }
  );

  const studentName = selectedStudent
    ? `${selectedStudent.firstName || selectedStudent.first_name || ''} ${selectedStudent.lastName || selectedStudent.last_name || ''}`.trim()
    : '';

  const parentEmail = selectedStudent?.email || '';

  const loadPreview = async () => {
    if (!form.country || !form.currency || !form.batchType || !form.subscriptionModel) {
      setPreview(null);
      return;
    }
    setPreviewLoading(true);
    setPreviewError('');
    try {
      const res = await financeAPI.previewSubscription({
        country: form.country,
        currency: form.currency,
        batchType: form.batchType,
        subscriptionModel: form.subscriptionModel,
        classesPerWeek: Number(form.classesPerWeek),
        bonusClasses: Number(form.bonusClasses) || 0,
        serviceStartDate: form.serviceStartDate || undefined,
        discounts: discounts.map((d) => ({
          discountType: d.discountType,
          value: Number(d.value),
          isPercentage: !!d.isPercentage,
          duration: d.duration || 'one_time',
          notes: d.notes || null,
        })),
        addOns: addOns.map((a) => ({
          addOnType: a.addOnType,
          quantity: Number(a.quantity) || 1,
        })),
      });
      setPreview(res.data?.data || res.data);
    } catch (err) {
      setPreview(null);
      setPreviewError(
        err.response?.data?.message || 'Could not load pricing preview'
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (!open || step < 2) return;
    const t = setTimeout(() => {
      loadPreview();
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step, form, discounts, addOns]);

  const canNext = useMemo(() => {
    if (step === 0) return !!selectedStudent;
    if (step === 1) {
      return (
        form.batchType &&
        form.country &&
        form.currency &&
        form.subscriptionModel &&
        form.classesPerWeek >= 1 &&
        form.serviceStartDate &&
        form.enrollmentDate
      );
    }
    if (step === 2) return !!preview && !previewError;
    if (step === 3) return !!preview && !previewError;
    return true;
  }, [step, selectedStudent, form, preview, previewError]);

  const finalFeeLabel =
    form.subscriptionModel === 'monthly' ? 'Final Monthly Fee' : 'Final Fee';

  const handleCountryChange = (country) => {
    setForm((prev) => ({
      ...prev,
      country,
      currency: countryToCurrency[country] || prev.currency,
    }));
  };

  const handleFinish = async () => {
    if (!selectedStudent?.id) return;
    setSubmitting(true);
    try {
      const res = await financeAPI.createSubscriptionFull({
        studentId: selectedStudent.id,
        batchType: form.batchType,
        country: form.country,
        currency: form.currency,
        subscriptionModel: form.subscriptionModel,
        classesPerWeek: Number(form.classesPerWeek),
        bonusClasses: Number(form.bonusClasses) || 0,
        serviceStartDate: form.serviceStartDate,
        enrollmentDate: form.enrollmentDate,
        discounts: discounts.map((d) => ({
          discountType: d.discountType,
          value: Number(d.value),
          isPercentage: !!d.isPercentage,
          duration: d.duration || 'one_time',
          notes: d.notes || null,
        })),
        addOns: addOns.map((a) => ({
          addOnType: a.addOnType,
          quantity: Number(a.quantity) || 1,
        })),
        notifyParent: !!notifyParent && !!parentEmail,
      });
      const data = res.data?.data || res.data;
      const parentNotify = data?.parentNotify;
      if (parentNotify?.sent) {
        toast.success('Subscription created and parent notified');
      } else if (notifyParent && parentNotify?.reason === 'no_email') {
        toast.success(
          'Subscription created successfully. Parent was not notified because no email address is available.'
        );
      } else if (notifyParent && !parentNotify?.sent) {
        toast.success(
          'Subscription created successfully. Parent was not notified (email could not be sent).'
        );
      } else {
        toast.success('Subscription created successfully');
      }
      onCreated?.(data);
      onClose?.();
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        (Array.isArray(err.response?.data?.errors)
          ? err.response.data.errors.join(', ')
          : 'Failed to create subscription');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const discountCatalog = discountCatalogQuery.data || [];
  const addOnCatalog = addOnCatalogQuery.data || [];

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        New subscription{studentName ? ` for ${studentName}` : ''}
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {step === 0 && (
          <Box>
            {lockStudent && selectedStudent ? (
              <Alert severity="info">
                Student: <strong>{studentName}</strong>
                {selectedStudent.displayId || selectedStudent.display_id
                  ? ` (${selectedStudent.displayId || selectedStudent.display_id})`
                  : ''}
              </Alert>
            ) : (
              <>
                <TextField
                  fullWidth
                  size="small"
                  label="Search students without subscription"
                  value={studentSearch}
                  onChange={(e) => setStudentSearch(e.target.value)}
                  sx={{ mb: 2 }}
                />
                {studentsQuery.isLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <Box sx={{ maxHeight: 280, overflow: 'auto' }}>
                    {(studentsQuery.data || []).map((s) => {
                      const name = `${s.firstName || ''} ${s.lastName || ''}`.trim();
                      const selected = selectedStudent?.id === s.id;
                      return (
                        <Box
                          key={s.id}
                          onClick={() => {
                            setSelectedStudent(s);
                            if (s.country) {
                              handleCountryChange(s.country);
                            }
                            setNotifyParent(!!s.email);
                          }}
                          sx={{
                            p: 1.5,
                            mb: 1,
                            borderRadius: 1,
                            border: 1,
                            borderColor: selected ? 'primary.main' : 'divider',
                            bgcolor: selected ? 'action.selected' : 'background.paper',
                            cursor: 'pointer',
                          }}
                        >
                          <Typography fontWeight={600}>{name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {s.displayId || '—'} · {s.email || 'No email'} · {s.country || '—'}
                          </Typography>
                        </Box>
                      );
                    })}
                    {!studentsQuery.isLoading && !(studentsQuery.data || []).length && (
                      <Typography color="text.secondary">No eligible students found.</Typography>
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>
        )}

        {step === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Batch type"
                value={form.batchType}
                onChange={(e) => setForm((p) => ({ ...p, batchType: e.target.value }))}
              >
                {batchTypes.map((b) => (
                  <MenuItem key={b.value} value={b.value}>
                    {b.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Country"
                value={form.country}
                onChange={(e) => handleCountryChange(e.target.value)}
              >
                {countryList.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Currency"
                value={form.currency}
                onChange={(e) => setForm((p) => ({ ...p, currency: e.target.value.toUpperCase() }))}
                inputProps={{ maxLength: 3 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Plan"
                value={form.subscriptionModel}
                onChange={(e) => setForm((p) => ({ ...p, subscriptionModel: e.target.value }))}
              >
                {subscriptionModels.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Classes per week"
                value={form.classesPerWeek}
                onChange={(e) =>
                  setForm((p) => ({ ...p, classesPerWeek: Number(e.target.value) }))
                }
                inputProps={{ min: 1, max: 7 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Bonus classes"
                value={form.bonusClasses}
                onChange={(e) =>
                  setForm((p) => ({ ...p, bonusClasses: Number(e.target.value) }))
                }
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Enrollment date"
                InputLabelProps={{ shrink: true }}
                value={form.enrollmentDate}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    enrollmentDate: e.target.value,
                    // Keep provisional service start aligned with enrollment until first class.
                    serviceStartDate: e.target.value || p.serviceStartDate,
                  }))
                }
                helperText="Invoice is created now. Billing cycle is provisional from enrollment."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Provisional service start"
                InputLabelProps={{ shrink: true }}
                value={form.serviceStartDate}
                onChange={(e) => setForm((p) => ({ ...p, serviceStartDate: e.target.value }))}
                helperText="Final billing start locks to the student's first present or makeup class."
              />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mt: 0.5 }}>
                Payment can happen before or after batch assignment by agreement. Class access is not
                blocked by unpaid invoices. Renewal dates move to the first attended class.
              </Alert>
            </Grid>
          </Grid>
        )}

        {step === 2 && (
          <Box>
            {previewLoading && <CircularProgress size={24} />}
            {previewError && <Alert severity="error">{previewError}</Alert>}
            {preview && (
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>Per-class rate</TableCell>
                    <TableCell align="right">
                      {formatCurrency(preview.pricePerSession, preview.currency)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Billing classes (cycle)</TableCell>
                    <TableCell align="right">{preview.billingClasses}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Classes included (incl. bonus)</TableCell>
                    <TableCell align="right">{preview.totalClasses}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Subtotal</TableCell>
                    <TableCell align="right">
                      {formatCurrency(preview.classesSubtotal, preview.currency)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            )}
          </Box>
        )}

        {step === 3 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Discounts
            </Typography>
            {discounts.map((d, idx) => (
              <Grid container spacing={1} key={idx} sx={{ mb: 1 }} alignItems="center">
                <Grid item xs={4}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Type"
                    value={d.discountType}
                    onChange={(e) => {
                      const type = e.target.value;
                      const cat = discountCatalog.find(
                        (c) => (c.type || c.discountType) === type
                      );
                      setDiscounts((prev) => {
                        const next = [...prev];
                        next[idx] = {
                          ...next[idx],
                          discountType: type,
                          isPercentage:
                            cat?.defaultIsPercentage ?? cat?.default_is_percentage ?? true,
                          value:
                            cat?.defaultValue ?? cat?.default_value ?? next[idx].value,
                        };
                        return next;
                      });
                    }}
                  >
                    {discountCatalog.map((c) => (
                      <MenuItem key={c.type} value={c.type}>
                        {c.label || c.type}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Value"
                    value={d.value}
                    onChange={(e) => {
                      setDiscounts((prev) => {
                        const next = [...prev];
                        next[idx] = { ...next[idx], value: e.target.value };
                        return next;
                      });
                    }}
                  />
                </Grid>
                <Grid item xs={3}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Mode"
                    value={d.isPercentage ? 'pct' : 'flat'}
                    onChange={(e) => {
                      setDiscounts((prev) => {
                        const next = [...prev];
                        next[idx] = {
                          ...next[idx],
                          isPercentage: e.target.value === 'pct',
                        };
                        return next;
                      });
                    }}
                  >
                    <MenuItem value="pct">%</MenuItem>
                    <MenuItem value="flat">Flat</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={2}>
                  <IconButton
                    onClick={() => setDiscounts((prev) => prev.filter((_, i) => i !== idx))}
                  >
                    <Delete />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Button
              size="small"
              startIcon={<Add />}
              onClick={() =>
                setDiscounts((prev) => [
                  ...prev,
                  {
                    discountType: discountCatalog[0]?.type || 'sales',
                    value: 0,
                    isPercentage: true,
                    duration: 'one_time',
                  },
                ])
              }
              sx={{ mb: 2 }}
            >
              Add discount
            </Button>

            <Typography variant="subtitle2" gutterBottom>
              Add-ons
            </Typography>
            {addOns.map((a, idx) => (
              <Grid container spacing={1} key={idx} sx={{ mb: 1 }} alignItems="center">
                <Grid item xs={6}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Add-on"
                    value={a.addOnType}
                    onChange={(e) => {
                      setAddOns((prev) => {
                        const next = [...prev];
                        next[idx] = { ...next[idx], addOnType: e.target.value };
                        return next;
                      });
                    }}
                  >
                    {addOnCatalog.map((c) => (
                      <MenuItem key={c.key || c.addOnType} value={c.key || c.addOnType}>
                        {c.label || c.key}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Qty"
                    value={a.quantity}
                    onChange={(e) => {
                      setAddOns((prev) => {
                        const next = [...prev];
                        next[idx] = { ...next[idx], quantity: e.target.value };
                        return next;
                      });
                    }}
                  />
                </Grid>
                <Grid item xs={2}>
                  <IconButton onClick={() => setAddOns((prev) => prev.filter((_, i) => i !== idx))}>
                    <Delete />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
            <Button
              size="small"
              startIcon={<Add />}
              onClick={() =>
                setAddOns((prev) => [
                  ...prev,
                  { addOnType: addOnCatalog[0]?.key || 'kit', quantity: 1 },
                ])
              }
              sx={{ mb: 2 }}
            >
              Add add-on
            </Button>

            {previewLoading && <CircularProgress size={20} />}
            {previewError && <Alert severity="error">{previewError}</Alert>}
            {preview && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Live amount due:{' '}
                <strong>{formatCurrency(preview.amountDue, preview.currency)}</strong>
              </Alert>
            )}
          </Box>
        )}

        {step === 4 && (
          <Box>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Confidence summary
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Confirm you are charging the right amount before creating the subscription.
            </Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell>Student</TableCell>
                  <TableCell align="right">{studentName || '—'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Country</TableCell>
                  <TableCell align="right">{form.country || '—'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Batch type</TableCell>
                  <TableCell align="right">{BATCH_LABELS[form.batchType] || form.batchType}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Classes / week</TableCell>
                  <TableCell align="right">{form.classesPerWeek} Classes/Week</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Plan</TableCell>
                  <TableCell align="right">
                    {PLAN_LABELS[form.subscriptionModel] || form.subscriptionModel}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Discount</TableCell>
                  <TableCell align="right">
                    {preview?.discounts?.length
                      ? preview.discounts
                          .map(
                            (d) =>
                              `${d.label || d.discountType}: ${
                                d.isPercentage ? `${d.value}%` : formatCurrency(d.value, form.currency)
                              }`
                          )
                          .join('; ')
                      : 'None'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Add-ons</TableCell>
                  <TableCell align="right">
                    {preview?.addOns?.length
                      ? preview.addOns
                          .map(
                            (a) =>
                              `${a.addOnType} × ${a.quantity}: ${formatCurrency(
                                a.lineTotal,
                                form.currency
                              )}`
                          )
                          .join('; ')
                      : 'None'}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Next due date</TableCell>
                  <TableCell align="right">{preview?.nextDueDate || '—'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Classes included</TableCell>
                  <TableCell align="right">{preview?.totalClasses ?? '—'}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" color="primary">
              {finalFeeLabel}:{' '}
              {preview
                ? formatCurrency(preview.amountDue, preview.currency)
                : '—'}
            </Typography>

            <FormControlLabel
              sx={{ mt: 2, display: 'block' }}
              control={
                <Checkbox
                  checked={notifyParent && !!parentEmail}
                  disabled={!parentEmail}
                  onChange={(e) => setNotifyParent(e.target.checked)}
                />
              }
              label={
                parentEmail
                  ? `Notify parent by email (${parentEmail})`
                  : 'Notify parent by email (no email on file — subscription will still be created)'
              }
            />
            {!parentEmail && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Parent was not notified because no email address is available (after create).
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        {step > 0 && (
          <Button onClick={() => setStep((s) => s - 1)} disabled={submitting}>
            Back
          </Button>
        )}
        {step < STEPS.length - 1 ? (
          <Button
            variant="contained"
            disabled={!canNext}
            onClick={() => setStep((s) => s + 1)}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={!canNext || submitting || !preview}
            onClick={handleFinish}
          >
            {submitting ? 'Creating…' : 'Create subscription'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
